/**
 * Application API Service
 *
 * API methods for application operations including creation, deployment, logs, and deletion.
 * Updated to use standardized API response format.
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from '../types/api.types';
import type {
  ApplicationSanitized,
  ApplicationCreateDTO,
  ApplicationUpdateDTO,
  ApplicationListQuery,
  ApplicationLogsQuery,
  ApplicationLogsResponse,
} from '../types/application.types';
import type { TwoFactorRequiredResponse } from '../types/auth.types';

// ============================================================================
// APPLICATION SERVICE
// ============================================================================

export const applicationService = {
  /**
   * List all applications for a tenant
   */
  listApplications: async (tenantId: string, query?: ApplicationListQuery): Promise<ApplicationSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<ApplicationSanitized>>(`/tenants/${tenantId}/applications`, {
      params: query,
    });
    return response.data.data;
  },

  /**
   * Get application by ID
   */
  getApplication: async (tenantId: string, applicationId: string): Promise<ApplicationSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<ApplicationSanitized>>(`/tenants/${tenantId}/applications/${applicationId}`);
    return response.data.data;
  },

  /**
   * Create a new application
   */
  createApplication: async (tenantId: string, data: ApplicationCreateDTO): Promise<ApplicationSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<ApplicationSanitized>>(`/tenants/${tenantId}/applications`, data);
    return response.data.data;
  },

  /**
   * Update application
   */
  updateApplication: async (tenantId: string, applicationId: string, data: ApplicationUpdateDTO): Promise<ApplicationSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<ApplicationSanitized>>(`/tenants/${tenantId}/applications/${applicationId}`, data);
    return response.data.data;
  },

  /**
   * Delete application
   * Requires 2FA verification. If no 2FA credentials provided, returns TwoFactorRequiredResponse.
   */
  deleteApplication: async (
    tenantId: string,
    applicationId: string,
    verificationCodeId?: string,
    verificationCode?: string
  ): Promise<{ message: string } | TwoFactorRequiredResponse> => {
    const body = verificationCodeId && verificationCode
      ? { verificationCodeId, verificationCode }
      : undefined;

    const response = await apiClient.delete<ApiSuccessResponse<TwoFactorRequiredResponse> | ApiActionResponse>(
      `/tenants/${tenantId}/applications/${applicationId}`,
      body ? { data: body } : undefined
    );

    // Check if 2FA is required (response contains data with twoFactorRequired flag)
    if (response.data.data && 'twoFactorRequired' in response.data.data) {
      return response.data.data as TwoFactorRequiredResponse;
    }

    return { message: response.data.message || 'Application deleted successfully' };
  },

  /**
   * Upload application code bundle
   * @deprecated Code bundles are now managed via CLI and Deployment entity.
   * This method is kept for backward compatibility but should not be used.
   */
  uploadCodeBundle: async (tenantId: string, applicationId: string, file: File, onProgress?: (progress: number) => void): Promise<{ message: string; codeBundle: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<{ message: string; codeBundle: string }>(
      `/tenants/${tenantId}/applications/${applicationId}/code`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        },
      }
    );
    return response.data;
  },

  /**
   * Deploy application
   * @deprecated Deployments are now managed via CLI and Deployment entity.
   * This method is kept for backward compatibility but should not be used.
   */
  deployApplication: async (tenantId: string, applicationId: string): Promise<ApplicationSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<ApplicationSanitized>>(`/tenants/${tenantId}/applications/${applicationId}/deploy`);
    return response.data.data;
  },

  /**
   * Query application logs
   */
  queryLogs: async (tenantId: string, applicationId: string, query?: ApplicationLogsQuery): Promise<ApplicationLogsResponse> => {
    const response = await apiClient.get<ApiSuccessResponse<ApplicationLogsResponse>>(`/tenants/${tenantId}/applications/${applicationId}/logs`, {
      params: query,
    });
    return response.data.data;
  },

  /**
   * Invoke application function (for testing)
   */
  invokeFunction: async (tenantId: string, applicationId: string, payload?: Record<string, unknown>): Promise<unknown> => {
    const response = await apiClient.post(`/tenants/${tenantId}/applications/${applicationId}/invoke`, {
      payload,
    });
    return response.data;
  },
};

export default applicationService;
